import axios from 'axios'
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '../auth/tokenStore'

const backendUrl = process.env.REACT_APP_BACKEND_URL
const DOMAIN = `${backendUrl}/api`

// One axios instance shared by every service module. The interceptors centralize bearer-token
// auth and transparent refresh, so individual services never touch tokens or handle 401s.
const apiClient = axios.create({ baseURL: DOMAIN })

apiClient.interceptors.request.use((config) => {
    const token = getAccessToken()
    if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
})

// Single-flight refresh: concurrent 401s share one /auth/refresh call so we rotate the
// refresh token exactly once (rotation revokes the previous token server-side).
let refreshPromise = null

function refreshTokens() {
    if (!refreshPromise) {
        const refreshToken = getRefreshToken()
        if (!refreshToken) {
            return Promise.reject(new Error('No refresh token'))
        }
        // Bare axios (not apiClient) so this request bypasses the interceptors below.
        refreshPromise = axios
            .post(`${DOMAIN}/auth/refresh`, { refreshToken })
            .then(({ data }) => {
                setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken })
                return data.accessToken
            })
            .finally(() => { refreshPromise = null })
    }
    return refreshPromise
}

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config
        const status = error.response?.status
        const isRefreshCall = original?.url?.includes('/auth/refresh')

        // On a single 401, try to rotate once and replay the original request.
        if (status === 401 && original && !original._retried && !isRefreshCall) {
            original._retried = true
            try {
                const accessToken = await refreshTokens()
                original.headers = original.headers || {}
                original.headers.Authorization = `Bearer ${accessToken}`
                return apiClient(original)
            } catch (refreshError) {
                // Refresh token missing/expired/reused → session is dead.
                clearTokens()
                if (window.location.pathname !== '/login') {
                    window.location.replace('/login')
                }
                return Promise.reject(refreshError)
            }
        }
        return Promise.reject(error)
    }
)

export default apiClient
